import React from "react";
import { AlertCircle } from "lucide-react";

export const RANKS = ["Best", "Good", "Weak", "Poor"];

// The coverage rule (proposal §6): every assessed competency needs at least this many
// questions, and a set needs at least this many competencies represented.
export const MIN_QUESTIONS_PER_COMPETENCY = 3;
export const MIN_COMPETENCIES = 2;

export const newOption = (rank, text = "") => ({ id: `o_${crypto.randomUUID()}`, text, rank });

export const newQuestion = (competencyId = "") => ({
  id: `q_${crypto.randomUUID()}`,
  competency_id: competencyId,
  scenario_text: "",
  options: RANKS.map((rank) => newOption(rank)),
});

export const InlineError = ({ msg }) =>
  msg ? (
    <p className="flex items-center gap-1.5 mt-1 text-xs text-red-400">
      <AlertCircle size={11} />
      {msg}
    </p>
  ) : null;

const RANK_STYLES = {
  Best: "border-emerald-400/40 bg-emerald-500/5",
  Good: "border-sky-400/40 bg-sky-500/5",
  Weak: "border-amber-400/40 bg-amber-500/5",
  Poor: "border-red-400/40 bg-red-500/5",
};
const RANK_TEXT = {
  Best: "text-emerald-400",
  Good: "text-sky-400",
  Weak: "text-amber-400",
  Poor: "text-red-400",
};

/**
 * One fixed row per rank (Best/Good/Weak/Poor) rather than a free "mark correct"
 * control — "each rank used exactly once" holds by construction instead of needing
 * its own check, and matches how the default question seed is itself authored.
 */
const QuestionEditor = ({ question, onChange, locked = false }) => {
  const setOptionText = (rank, text) =>
    onChange({
      ...question,
      options: question.options.map((o) => (o.rank === rank ? { ...o, text } : o)),
    });

  return (
    <div className="space-y-1.5">
      {question.options.map((option) => (
        <div
          key={option.id}
          className={`flex items-center gap-2.5 px-3 py-1.5 rounded-xl border transition-all duration-150 ${
            RANK_STYLES[option.rank] || "border-white/10 bg-bgSecondary dark:glass-effect"
          }`}
        >
          <span id={`rank-label-${option.id}`} className={`text-sm font-semibold w-10 shrink-0 ${RANK_TEXT[option.rank] || "dark:text-gray-500 text-textSecondary"}`}>
            {option.rank}
          </span>
          <input
            type="text"
            aria-labelledby={`rank-label-${option.id}`}
            placeholder={`What a "${option.rank}" response looks like...`}
            value={option.text}
            onChange={(e) => setOptionText(option.rank, e.target.value)}
            maxLength={200}
            disabled={locked}
            className={`flex-1 min-w-0 rounded-lg border dark:text-white text-textPrimary text-sm px-2.5 py-1.5 outline-none transition-colors placeholder:dark:text-gray-600 placeholder:text-gray-400 border-gray-300 dark:border-white/10 focus:border-purple-400 bg-bgPrimary dark:glass-effect ${locked ? "opacity-60 cursor-not-allowed" : ""}`}
          />
        </div>
      ))}
    </div>
  );
};

export default QuestionEditor;
